import type { AdditionalCostsData } from "@wayflyer/flyui-dataviz";

import { hasAdditionalCosts } from "../utils/metrics";
import type { DashboardMetadata } from "../utils/fetch-dashboard-metadata";
import { channelMapping } from "../strings/tooltips";
import type { DashboardOutput, DataPoint } from "../utils/fetch-api";
import { Dimensions, MetricTypes } from "../autogen/metrics";

const getExpectedRefunds = (
  nonRefundData: DataPoint[],
  historicChannelsData: DataPoint[],
  channel_level_total_sales_metric: MetricTypes,
  total_sales_inc_refunds_metric: MetricTypes
) => {
  return nonRefundData.map((currentData) => {
    const historicData = historicChannelsData.find((row) => row["Channel"] === currentData["Channel"]);
    if (!historicData) {
      return 0;
    }
    const currentTotalSales = +currentData[channel_level_total_sales_metric];
    const historicTotalSales = +historicData[channel_level_total_sales_metric];
    const historicTotalSalesIncRefunds = +historicData[total_sales_inc_refunds_metric];
    const historicRefunds = historicTotalSalesIncRefunds - historicTotalSales;
    if (historicTotalSales === 0) {
      return 0;
    } else {
      return (historicRefunds / historicTotalSales) * currentTotalSales;
    }
  });
};

const augmentWithRefundsData = (
  currentChannelsData: DataPoint[],
  historicChannelsData: DataPoint[],
  channel_level_total_sales_metric: MetricTypes,
  total_sales_inc_refunds_metric: MetricTypes,
  totalRefunds: number,
  total_sales_metric: MetricTypes
) => {
  const nonRefundData = currentChannelsData.filter((row) => row["Channel"] !== "Refunds");
  const expectedRefundsArr = getExpectedRefunds(
    nonRefundData,
    historicChannelsData,
    channel_level_total_sales_metric,
    total_sales_inc_refunds_metric
  );

  const totalExpectedRefunds = expectedRefundsArr.reduce((a, b) => a + b, 0);

  return nonRefundData.map((currentData, index) => {
    const expectedRefunds = expectedRefundsArr[index];

    const currentTotalSalesIncRefunds = +currentData[total_sales_inc_refunds_metric];
    let totalActualRefunds = 0;
    let totalActualRefundsPct = 0;
    let totalActualSales = currentTotalSalesIncRefunds;

    if (totalExpectedRefunds !== 0) {
      totalActualRefunds = (expectedRefunds / totalExpectedRefunds) * totalRefunds;
      totalActualSales = currentTotalSalesIncRefunds - totalActualRefunds;
      if (totalActualSales !== 0) {
        totalActualRefundsPct = (100 * totalActualRefunds) / totalActualSales;
      }
    }

    const augmented = { ...currentData };

    augmented[total_sales_metric] = totalActualSales;
    augmented[MetricTypes.REFUNDS_PERCENT] = totalActualRefundsPct;

    return augmented;
  });
};

const updateSummaryWithRefunds = (
  row: DataPoint,
  total_sales_inc_refunds_metric: MetricTypes,
  totalRefunds: number,
  total_sales_metric: MetricTypes
) => {
  const newSummary = { ...row };
  const overviewTotalSalesIncRefunds = +row[total_sales_inc_refunds_metric];
  const totalActualSales = overviewTotalSalesIncRefunds - totalRefunds;
  let totalRefundsPct = 0;
  if (totalActualSales !== 0) {
    totalRefundsPct = (100 * totalRefunds) / totalActualSales;
  }

  newSummary[total_sales_metric] = totalActualSales;
  newSummary[MetricTypes.REFUNDS_PERCENT] = totalRefundsPct;

  return newSummary;
};

function augmentWithAdditionalCosts(
  refundAugmentedData: DataPoint[],
  total_sales_metric: MetricTypes,
  additionalCosts,
  useAdditionalCosts
) {
  return refundAugmentedData.map((currentData) => {
    const currentQuantity = +currentData[MetricTypes.TOTAL_ORDER_QUANTITY];
    const currentTotalSales = +currentData[total_sales_metric];
    const cogs = currentQuantity * additionalCosts.cogs;

    const profit = currentTotalSales - currentQuantity * (additionalCosts.cogs + additionalCosts.shipping);
    const margin = currentTotalSales ? (100 * (currentTotalSales - cogs)) / currentTotalSales : 0;

    const augmented = { ...currentData };
    if (useAdditionalCosts) {
      augmented[MetricTypes.COGS] = cogs;
      augmented[MetricTypes.GROSS_PROFIT] = profit;
      augmented[MetricTypes.GROSS_MARGIN_PERCENTAGE] = margin;
    }
    return augmented;
  });
}

const getAugmentedData = (
  currentData: DataPoint[],
  historicData: DataPoint[],
  channel_level_total_sales_metric: MetricTypes,
  total_sales_inc_refunds_metric: MetricTypes,
  total_sales_metric: MetricTypes,
  totalRefunds: number,
  additionalCosts,
  useAdditionalCosts: boolean
) => {
  const augmented = augmentWithRefundsData(
    currentData,
    historicData,
    channel_level_total_sales_metric,
    total_sales_inc_refunds_metric,
    totalRefunds,
    total_sales_metric
  );

  const costAugmentedData = augmentWithAdditionalCosts(
    augmented,
    total_sales_metric,
    additionalCosts,
    useAdditionalCosts
  );

  costAugmentedData.forEach((row) => {
    // Rename channels
    const channel = row[Dimensions.CHANNEL];
    const alias = channelMapping[channel];
    if (alias) {
      row[Dimensions.CHANNEL] = alias;
    }
    // Stupid Percentage nonsense
    row[MetricTypes.NEW_CUSTOMERS] = 100 * +row[MetricTypes.NEW_CUSTOMERS];
  });

  return costAugmentedData;
};

const getAugmentedSummary = (
  currentSummary: DataPoint,
  channel_level_total_sales_metric: MetricTypes,
  total_sales_inc_refunds_metric: MetricTypes,
  total_sales_metric: MetricTypes,
  totalRefunds: number,
  additionalCosts,
  useAdditionalCosts: boolean
) => {
  const newSummary = updateSummaryWithRefunds(
    currentSummary,
    total_sales_inc_refunds_metric,
    totalRefunds,
    total_sales_metric
  );

  const withCosts = augmentWithAdditionalCosts(
    [newSummary],
    total_sales_metric,
    additionalCosts,
    useAdditionalCosts
  )[0];

  // Stupid Percentage nonsense
  withCosts[MetricTypes.NEW_CUSTOMERS] = 100 * +withCosts[MetricTypes.NEW_CUSTOMERS];

  return withCosts;
};

export const getMetricsFromMetadata = (metadata: DashboardMetadata) => {
  const total_sales_metric = metadata.supports_refunds_by_refunded_date
    ? MetricTypes.GROSS_REVENUE_LESS_DISCOUNTS_LESS_REFUNDS_BY_REFUNDED_DATE_PLUS_SHIPPING_PLUS_TAX
    : MetricTypes.GROSS_REVENUE_LESS_DISCOUNTS_LESS_REFUNDS_FROM_ORDERS_PLUS_SHIPPING_PLUS_TAX;

  const channel_level_total_sales_metric =
    MetricTypes.GROSS_REVENUE_LESS_DISCOUNTS_LESS_REFUNDS_FROM_ORDERS_PLUS_SHIPPING_PLUS_TAX;
  const total_sales_inc_refunds_metric = MetricTypes.GROSS_REVENUE_LESS_DISCOUNTS_PLUS_SHIPPING_PLUS_TAX;
  return { total_sales_metric, channel_level_total_sales_metric, total_sales_inc_refunds_metric };
};

export const constructChannelsData = (
  metadata: DashboardMetadata,
  additionalCosts: AdditionalCostsData,
  overviewDashboardOutput: DashboardOutput,
  currentDashboardOutput: DashboardOutput,
  historicDashboardOutput: DashboardOutput
): DashboardOutput => {
  const { total_sales_metric, channel_level_total_sales_metric, total_sales_inc_refunds_metric } =
    getMetricsFromMetadata(metadata);

  const useAdditionalCosts = metadata.supports_profit_analysis && hasAdditionalCosts(additionalCosts);

  const totalRefunds =
    (overviewDashboardOutput.summary[total_sales_inc_refunds_metric] as number) -
    (overviewDashboardOutput.summary[total_sales_metric] as number);

  const additionalCostsAugmented = getAugmentedData(
    currentDashboardOutput.data,
    historicDashboardOutput.data,
    channel_level_total_sales_metric,
    total_sales_inc_refunds_metric,
    total_sales_metric,
    totalRefunds,
    additionalCosts,
    useAdditionalCosts
  );

  const augmentedSummary = getAugmentedSummary(
    currentDashboardOutput.summary,
    channel_level_total_sales_metric,
    total_sales_inc_refunds_metric,
    total_sales_metric,
    totalRefunds,
    additionalCosts,
    useAdditionalCosts
  );

  const newDashOutput = { ...currentDashboardOutput };

  newDashOutput.data = additionalCostsAugmented;
  newDashOutput.summary = augmentedSummary;

  return newDashOutput;
};
